import { ChangeEvent, useEffect, useState } from "react";
import { useClinicContext } from "../../contexts";
import { BPDoctor, Clinic, DoctorRoomAllocation } from "../../models";
import { InputNumber, SelectControl } from "../Common";
import { ConfirmReduceRooms } from "./ConfirmReduceRooms";
import { ConfirmDeleteAllocation } from "./ConfirmDeleteAllocation";

const dayOfWeeks = [
	{ value: 1, label: "Monday" },
	{ value: 2, label: "Tuesday" },
	{ value: 3, label: "Wednesday" },
	{ value: 4, label: "Thursday" },
	{ value: 5, label: "Friday" },
	{ value: 6, label: "Saturday" },
	{ value: 0, label: "Sunday" }
];

interface props {
	clinic: Clinic;
	doctorRoomAllocationData: DoctorRoomAllocation[];
	doctors: BPDoctor[];
	handleClinicChange: (value: string) => void;
	getDoctorRoomAllocationData: () => void;
	saveDoctorRoomAllocation: (
		doctorRoomAllocation: DoctorRoomAllocation
	) => Promise<void>;
	cellMessages: Record<string, { message: string; type: "error" | "success" }>;
	setCellMessages: React.Dispatch<
		React.SetStateAction<
			Record<string, { message: string; type: "error" | "success" }>
		>
	>;
}

export const RoomAllocationTable = ({
	clinic,
	doctorRoomAllocationData,
	doctors,
	handleClinicChange,
	getDoctorRoomAllocationData,
	saveDoctorRoomAllocation,
	cellMessages,
	setCellMessages
}: props) => {
	const { userClinics } = useClinicContext();
	const [editRoomAllocation, setEditRoomAllocation] = useState<
		DoctorRoomAllocation | undefined
	>(undefined);
	const [addRoomAllocation, setAddRoomAllocation] = useState<
		DoctorRoomAllocation | undefined
	>(undefined);

	const [roomNumbers, setRoomNumbers] = useState<number[]>([]);
	const [deleteAllocations, setDeleteAllocations] = useState<
		DoctorRoomAllocation[]
	>([]);
	const [openConfirmReduceRoom, setOpenConfirmReduceRoom] = useState(false);
	const [openConfirmDeleteAllocation, setOpenConfirmDeleteAllocation] =
		useState(false);
	const [allocationToDelete, setAllocationToDelete] = useState<
		DoctorRoomAllocation | undefined
	>(undefined);

	useEffect(() => {
		if (doctorRoomAllocationData.length === 0) {
			setRoomNumbers([]);
		}
		const uniqueValues = new Set(
			doctorRoomAllocationData.map((r) => r.roomNumber)
		);
		const maxRoomNumber = Math.max(...[...uniqueValues]);
		setRoomNumbers(
			Array.from({ length: maxRoomNumber }, (_, index) => index + 1)
		);
	}, [doctorRoomAllocationData]);

	useEffect(() => {
		if (cellMessages && Object.keys(cellMessages).length > 0) {
			setTimeout(() => {
				const roomNumber = Object.keys(cellMessages)[0].substring(
					0,
					Object.keys(cellMessages)[0].indexOf("-")
				);
				const row = document.getElementById(`row-${roomNumber}`);
				if (row) {
					row.scrollIntoView({ behavior: "smooth" });
				}
			}, 500);
		}
	}, [cellMessages]);

	const handleRoomNumberChange = async (
		event: ChangeEvent<HTMLInputElement>
	) => {
		const newRoomCount = Number(event.target.value);
		if (!newRoomCount) return;

		if (newRoomCount > roomNumbers.length) {
			const additionalRooms = Array.from(
				{ length: newRoomCount - roomNumbers.length },
				(_, i) => roomNumbers.length + i + 1
			);

			setRoomNumbers([...roomNumbers, ...additionalRooms]);
			return;
		}

		if (newRoomCount < roomNumbers.length) {
			const removedRooms = roomNumbers.slice(newRoomCount);

			const dataExists = doctorRoomAllocationData?.filter(
				(doctorRoomAllocation) =>
					removedRooms.includes(doctorRoomAllocation.roomNumber) &&
					doctorRoomAllocation.id > 0
			);

			if (dataExists?.length != null && dataExists?.length > 0) {
				setDeleteAllocations(dataExists);
				setOpenConfirmReduceRoom(true);
			} else {
				setRoomNumbers([...roomNumbers.splice(0, newRoomCount)]);
			}
		}
	};

	const handleCloseConfirmReduceRoom = async (updated: boolean) => {
		if (updated && (allocationToDelete || deleteAllocations)) {
			const deleted = allocationToDelete
				? allocationToDelete
				: deleteAllocations[0];
			setCellMessage(
				deleted.roomNumber,
				deleted.dayOfWeekNumber,
				"Deleted",
				"success"
			);
			getDoctorRoomAllocationData();
		}
		setDeleteAllocations([]);
		setOpenConfirmReduceRoom(false);
		setOpenConfirmDeleteAllocation(false);
	};

	const copyAllocationRight = async (roomAllocation?: DoctorRoomAllocation) => {
		if (!roomAllocation) {
			return;
		}

		const dayOfWeekNumber =
			roomAllocation.dayOfWeekNumber <= 5
				? roomAllocation.dayOfWeekNumber + 1
				: 0;

		const allocations = findDoctorRoomAllocations(
			dayOfWeekNumber,
			undefined,
			roomAllocation.doctorInstanceId
		);

		if (allocations.length > 0) {
			setCellMessage(
				roomAllocation.roomNumber,
				roomAllocation.dayOfWeekNumber,
				`${roomAllocation.doctorName} already has a room allocated for this day`,
				"error"
			);
			setEditRoomAllocation(undefined);
			setAddRoomAllocation(undefined);
			return;
		}

		const newDoctorRoomAllocation = {
			...roomAllocation,
			id: 0,
			dayOfWeekNumber:
				roomAllocation.dayOfWeekNumber <= 5
					? roomAllocation.dayOfWeekNumber + 1
					: 0
		};
		await saveDoctorRoomAllocation(newDoctorRoomAllocation);
		setCellMessage(
			newDoctorRoomAllocation.roomNumber,
			newDoctorRoomAllocation.dayOfWeekNumber,
			"Saved",
			"success"
		);
	};

	const handleDeleteAllocation = async (
		doctorRoomAllocation?: DoctorRoomAllocation
	) => {
		if (!doctorRoomAllocation) {
			return;
		}
		setOpenConfirmDeleteAllocation(true);
		setAllocationToDelete(doctorRoomAllocation);
	};

	const handleDoctorChange = async (value: string) => {
		if (editRoomAllocation) {
			if (!value || value === editRoomAllocation?.doctorInstanceId.toString()) {
				setEditRoomAllocation(undefined);
				return;
			}
		}

		const dayOfWeekNumber =
			editRoomAllocation?.dayOfWeekNumber ??
			addRoomAllocation?.dayOfWeekNumber ??
			-1;

		const allocations = findDoctorRoomAllocations(
			dayOfWeekNumber,
			undefined,
			Number(value)
		);

		const doctor = doctors.find((doctor) => doctor.id === Number(value));
		if (doctor) {
			if (allocations.length > 0) {
				setCellMessage(
					editRoomAllocation?.roomNumber ?? addRoomAllocation?.roomNumber ?? 0,
					dayOfWeekNumber,
					`${doctor.fullName} already has a room allocated for this day`,
					"error"
				);
				setEditRoomAllocation(undefined);
				setAddRoomAllocation(undefined);
				return;
			}

			const newAllocation = editRoomAllocation
				? ({
						...editRoomAllocation,
						doctorInstanceId: doctor.id
					} as unknown as DoctorRoomAllocation)
				: ({
						...addRoomAllocation,
						doctorInstanceId: doctor.id
					} as unknown as DoctorRoomAllocation);
			setEditRoomAllocation(undefined);
			setAddRoomAllocation(undefined);
			await saveDoctorRoomAllocation(newAllocation);
			setCellMessage(
				newAllocation.roomNumber,
				newAllocation.dayOfWeekNumber,
				"Saved",
				"success"
			);
		}
	};

	const setCellMessage = (
		roomNumber: number,
		dayOfWeekNumber: number,
		message: string,
		type: "error" | "success"
	) => {
		const key = `${roomNumber}-${dayOfWeekNumber}`;

		setCellMessages((prev) => ({
			...prev,
			[key]: { message, type }
		}));
		setTimeout(() => {
			clearCellMessage(roomNumber, dayOfWeekNumber);
		}, 5000);
	};

	const clearCellMessage = (roomNumber: number, dayOfWeekNumber: number) => {
		const key = `${roomNumber}-${dayOfWeekNumber}`;

		setCellMessages((prev) => {
			const updated = { ...prev };
			delete updated[key];
			return updated;
		});
	};

	const handleDoctorBlur = () => {
		setEditRoomAllocation(undefined);
		setAddRoomAllocation(undefined);
	};

	const handleEditClick = (doctorRoomAllocation?: DoctorRoomAllocation) => {
		if (doctorRoomAllocation?.id != null && doctorRoomAllocation?.id > 0) {
			setEditRoomAllocation(doctorRoomAllocation);
			setAddRoomAllocation(undefined);
		} else {
			setAddRoomAllocation(doctorRoomAllocation);
			setEditRoomAllocation(undefined);
		}
	};

	const findDoctorRoomAllocations = (
		dayOfWeekNumber: number,
		roomNumber?: number,
		doctorInstanceId?: number
	) => {
		return (
			doctorRoomAllocationData?.filter(
				(dra) =>
					dra.dayOfWeekNumber === dayOfWeekNumber &&
					(!roomNumber || dra.roomNumber === roomNumber) &&
					(!doctorInstanceId || dra.doctorInstanceId === doctorInstanceId)
			) ?? []
		);
	};

	const renderTableCell = (roomNumber: number, dayOfWeekNumber: number) => {
		const doctorRoomAllocations = findDoctorRoomAllocations(
			dayOfWeekNumber,
			roomNumber
		);
		if (doctorRoomAllocations.length === 0) {
			doctorRoomAllocations.push({
				id: 0,
				clinicId: clinic.id,
				roomNumber,
				dayOfWeekNumber,
				doctorInstanceId: 0
			} as unknown as DoctorRoomAllocation);
		}

		return (
			<>
				{doctorRoomAllocations.map((dra, index) => {
					let isEdit =
						editRoomAllocation &&
						editRoomAllocation.id === dra.id &&
						editRoomAllocation.id !== 0;

					const isAdd =
						addRoomAllocation &&
						addRoomAllocation.roomNumber === roomNumber &&
						addRoomAllocation.dayOfWeekNumber === dayOfWeekNumber &&
						index === doctorRoomAllocations.length - 1;
					if (isAdd) {
						isEdit = false;
					}
					const displayCopyRightIcon = !isEdit && dra.id > 0;

					const displayAddIcon =
						!isEdit && !isAdd && index === doctorRoomAllocations.length - 1;
					const displayDeleteIcon = !isEdit && dra.id > 0;
					return (
						<>
							{!isEdit && (!isAdd || dra.id > 0) && (
								<div key={dra.id} className="flex justify-between mb-2">
									<button
										className="ch-button-link align-left w-full"
										onClick={() => handleEditClick(dra)}
									>
										{dra.doctorName}
									</button>
									<div className="flex flex-col justify-between">
										<div className="flex pt-3 pb-2">
											{displayDeleteIcon && (
												<img
													src="images/svg/delete-blue-outline.svg"
													alt="Delete"
													width="16"
													height="16"
													style={{
														marginLeft: 4,
														cursor: "pointer",
														minWidth: 16,
														minHeight: 16
													}}
													onClick={() => handleDeleteAllocation(dra)}
												/>
											)}

											{displayCopyRightIcon && (
												<img
													src="images/svg/person-add-chevron-right-blue.svg"
													alt="copy right"
													width="20"
													height="20"
													style={{ marginLeft: 4, cursor: "pointer" }}
													onClick={() => copyAllocationRight(dra)}
												/>
											)}
										</div>
									</div>
								</div>
							)}

							{(isEdit || isAdd) && doctors && (
								<div style={{ marginLeft: 8, marginTop: 8 }}>
									<SelectControl
										id="select-doctor"
										label="Doctor"
										value={(
											editRoomAllocation?.doctorInstanceId ??
											addRoomAllocation?.doctorInstanceId ??
											0
										).toString()}
										options={doctors.map((doctor) => ({
											value: doctor.id.toString(),
											label: doctor.fullName
										}))}
										handleChange={handleDoctorChange}
										handleBlur={handleDoctorBlur}
									/>
								</div>
							)}

							{displayAddIcon && (
								<div className="flex justify-end">
									<img
										src="images/svg/add-circle-blue.svg"
										alt="Add"
										width="16"
										height="16"
										style={{ marginLeft: 4, cursor: "pointer" }}
										onClick={() => {
											setAddRoomAllocation({
												id: 0,
												clinicId: clinic.id,
												roomNumber,
												dayOfWeekNumber,
												doctorInstanceId: 0
											} as unknown as DoctorRoomAllocation);
										}}
									/>
								</div>
							)}
						</>
					);
				})}
			</>
		);
	};

	return (
		<>
			<div className="flex p-4">
				<div style={{ width: 400 }}>
					<SelectControl
						id="select-clinic"
						label="Centre"
						value={clinic ? (clinic as Clinic).id.toString() : ""}
						options={userClinics?.map((clinic) => ({
							value: clinic.id.toString(),
							label: clinic.name
						}))}
						handleChange={handleClinicChange}
					/>
				</div>
				<div className="flex flex-col">
					<div>Number of rooms</div>
					<InputNumber
						name="numberOfRooms"
						value={roomNumbers ? roomNumbers.length : 0}
						handleChange={handleRoomNumberChange}
					/>
				</div>
			</div>
			<div className="p-4">
				{doctorRoomAllocationData && (
					<table className="ch-table w-full">
						<tr>
							<th>Room Number</th>
							{dayOfWeeks.map((day) => (
								<th>{day.label}</th>
							))}
						</tr>
						<tbody>
							{roomNumbers.map((roomNumber) => (
								<tr key={roomNumber}>
									<td id={`row-${roomNumber}`}>{roomNumber}</td>
									{dayOfWeeks.map((day) => {
										const key = `${roomNumber}-${day.value}`;
										const cellMessage = cellMessages[key];

										return (
											<td style={{ paddingLeft: 0 }}>
												{renderTableCell(roomNumber, day.value)}

												{cellMessage && (
													<div
														style={{
															fontSize: cellMessage.type === "error" ? 14 : 16,
															marginTop: 4,
															color:
																cellMessage.type === "error"
																	? "#d32f2f"
																	: "#2e7d32"
														}}
													>
														{cellMessage.message}
													</div>
												)}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
			<ConfirmReduceRooms
				open={openConfirmReduceRoom}
				allocationsToRemove={deleteAllocations}
				onClose={handleCloseConfirmReduceRoom}
			/>
			<ConfirmDeleteAllocation
				open={openConfirmDeleteAllocation}
				doctorRoomAllocation={allocationToDelete}
				onClose={handleCloseConfirmReduceRoom}
			/>
		</>
	);
};
